import { Trans } from '@lingui/macro'
import { RouteConfig } from 'app/router/Routes'
import sfOauthConfig from 'app/services/sfAuth/sfAuthConfig'
import Loading from 'egret/components/EgretLoadable/Loading'
import { Redirect } from 'react-router-dom'
import { EgretLoadable } from '../../../egret'
import {
  ALLOCATION_COMMITTEE_GROUP,
  authRoles
} from '../../auth/authRoles'
import { NOT_FOUND_PATH } from '../sessions/SessionRoutes'
import Committees from './committees/Committees'
import EditCommittee from './committees/EditCommittee'
import { GRANTS_PATH } from './GrantsConstants'

const OrganisationDetails = EgretLoadable({
  loader: async () => await import('./organization/OrganisationDetails')
})

const JoinOrganization = EgretLoadable({
  loader: async () => await import('../join-organization-flow/JoinOrganization')
})

const ReviewApplications = EgretLoadable({
  loader: async () => await import('./committees/ReviewApplications')
})

const Homepage = EgretLoadable({
  loader: async () => await import('./grantsHome/GrantsHome')
})

const WhereToStart = EgretLoadable({
  loader: async () => await import('./WhereToStart')
})

const ServiceInventory = EgretLoadable({
  loader: async () => await import('./ServiceInventory')
})

const EditService = EgretLoadable({
  loader: async () => await import('./EditService'),
  loading: () => <Loading atCenter />
})

const ApplicationEdit = EgretLoadable({
  loader: async () =>
    await import('./applications/old-application/ApplicationEdit')
})

const ActionPlan = EgretLoadable({
  loader: async () => await import('./action-plan/ActionPlan')
})

// const Reports = EgretLoadable({
//   loader: async () => await import('./grantee-reports/Reports')
// })

const ExternalReviewSurvey = EgretLoadable({
  loader: async () => await import('./external-reviewer/ExternalReviewSurvey')
})

const AcceptReviewRequest = EgretLoadable({
  loader: async () => await import('./external-reviewer/ReviewerAccept')
})

const Calendar = EgretLoadable({
  loader: async () => await import('./calendar/UserCalendar')
})

const FAQTab = EgretLoadable({
  loader: async () => await import('./faqs/FAQTab')
})

const FAQEditor = EgretLoadable({
  loader: async () => await import('./faqs/FAQEditor')
})

const AboutMe = EgretLoadable({
  loader: async () => await import('./external-reviewer/AboutMe')
})

const ApplyToExternalReview = EgretLoadable({
  loader: async () => await import('./external-reviewer/ApplyToExternalReview')
})

const AssignedOpportunities = EgretLoadable({
  loader: async () => await import('./external-reviewer/AssignedOpportunities')
})

const ApplicantInformation = EgretLoadable({
  loader: async () => await import('./ApplicantInformation')
})

const ActionTemplates = EgretLoadable({
  loader: async () => await import('./ActionTemplates')
})

const ActionTemplate = EgretLoadable({
  loader: async () => await import('./ActionTemplate')
})

const PrintView = EgretLoadable({
  loader: async () => await import('./applications/old-application/PrintView')
})

const Organizations = EgretLoadable({
  loader: async () => await import('./organization/UserOrganizations')
})

const ManageOrganizationMembers = EgretLoadable({
  loader: async () => await import('./organization/ManageOrganizationMembers')
})

const TechnicalAdvisories = EgretLoadable({
  loader: async () => await import('./TechnicalAdvisories')
})

const EligibilityRecheck = EgretLoadable({
  loader: async () => await import('../grants/EligibilityRecheck')
})

const ExecutiveSummaries = EgretLoadable({
  loader: async () => await import('./committees/ExecutiveSummaries')
})

const ConflictOfInterest = EgretLoadable({
  loader: async () => await import('./committees/ConflictOfInterest')
})

const grantsRoutes: RouteConfig[] = [
  {
    path: `${GRANTS_PATH.ORGANIZATION_DETAILS}/:id?`,
    component: OrganisationDetails,
    showCollapseBtn: true,
    collapseByDefault: true
  },
  {
    path: `${GRANTS_PATH.COMMITTEES}/:initialTab?`,
    component: Committees
  },
  {
    path: `${GRANTS_PATH.WORKGROUPS}/:initialTab?`,
    component: Committees,
    isTranslatable: true,
    label: <Trans>GRANTS_ROUTES_WORKGROUPS_LABEL</Trans>
  },
  {
    path: `${GRANTS_PATH.EDIT_COMMITTEE}/:id`,
    component: EditCommittee
  },
  {
    path: `${GRANTS_PATH.EDIT_WORKGROUP}/:id`,
    component: EditCommittee,
    isTranslatable: true,
    label: <Trans>GRANTS_ROUTES_EDIT_WORKGROUP_LABEL</Trans>
  },
  {
    path: GRANTS_PATH.TECHNICAL_ADVISORIES,
    component: TechnicalAdvisories,
    auth: authRoles.grantee
  },
  {
    path: GRANTS_PATH.ACTION_TEMPLATES,
    component: ActionTemplates,
    auth: authRoles.editor,
    isTranslatable: true,
    label: <Trans>GRANTS_ROUTES_ACTION_TEMPLATES_LABEL</Trans>
  },
  {
    path: `${GRANTS_PATH.ACTION_TEMPLATE}/:id`,
    component: ActionTemplate,
    auth: authRoles.editor,
    isTranslatable: true,
    label: <Trans>GRANTS_ROUTES_ACTION_TEMPLATE_EDITOR_LABEL</Trans>
  },
  {
    path: GRANTS_PATH.HOME,
    component: Homepage,
    isTranslatable: true,
    label: <Trans>GRANTS_ROUTES_HOMEPAGE_LABEL</Trans>,
    showCollapseBtn: true
  },
  {
    path: '/grants/where-to-start',
    component: WhereToStart,
    isTranslatable: true,
    label: <Trans>GRANTS_ROUTES_WHERE_TO_START_LABEL</Trans>,
    showCollapseBtn: true
  },
  {
    path: `${GRANTS_PATH.EXECUTIVE_SUMMARIES}/:committee`,
    component: ExecutiveSummaries,
    isTranslatable: true,
    label: <Trans>GRANTS_ROUTES_SC_EXECUTIVE_SUMMARIES_LABEL</Trans>,
    showCollapseBtn: true
  },
  {
    path: '/grants/conflict-of-interest/:committee/:id',
    component: ConflictOfInterest,
    isTranslatable: true,
    label: <Trans>GRANTS_ROUTES_CONFLICT_OF_INTEREST_LABEL</Trans>,
    showCollapseBtn: true
  },
  {
    path: GRANTS_PATH.SERVICE_INVENTORY,
    component: ServiceInventory,
    auth: authRoles.admin
  },
  {
    path: `${GRANTS_PATH.EDIT_SERVICE}/:id`,
    component: EditService,
    auth: authRoles.admin
  },
  {
    path: `${GRANTS_PATH.REVIEW_APPLICATION}/:committee/:id`,
    component: ReviewApplications,
    isTranslatable: true,
    label: <Trans>GRANTS_ROUTES_REVIEW_APPLICATION_LABEL</Trans>,
    showCollapseBtn: true,
    collapseByDefault: true,
    errorRedirectPath: GRANTS_PATH.HOME
  },
  {
    path: `${GRANTS_PATH.APPLICATION}/:id`,
    component: ApplicationEdit,
    auth: authRoles.grantee,
    isTranslatable: true,
    label: <Trans>GRANTS_ROUTES_OLD_APPLICATION_FORM_LABEL</Trans>
  },
  {
    path: `${GRANTS_PATH.ELIGIBILITY_RECHECK}/:id`,
    component: EligibilityRecheck,
    auth: authRoles.grantee,
    isTranslatable: true,
    label: <Trans>GRANTS_ROUTES_ELIGIBILITY_RECHECK_LABEL</Trans>
  },
  {
    path: GRANTS_PATH.CALENDAR,
    component: Calendar,
    auth: authRoles.grantee,
    isTranslatable: true,
    label: <Trans>GRANTS_ROUTES_CALENDAR_LABEL</Trans>
  },
  {
    path: GRANTS_PATH.ACTION_PLAN,
    component: ActionPlan,
    auth: authRoles.grantee,
    isTranslatable: true,
    label: <Trans>GRANTS_ROUTES_ACTION_PLAN_LABEL</Trans>,
    showCollapseBtn: true,
    collapseByDefault: true
  },

  {
    path: GRANTS_PATH.REVIEWER_PROFILE,
    component: AboutMe,
    label: <Trans>NAVIGATIONS_REVIEWER_PROFILE</Trans>,
    showCollapseBtn: true,
    collapseByDefault: true
  },
  {
    path: GRANTS_PATH.APPLY_TO_EXTERNAL_REVIEWER,
    component: ApplyToExternalReview,
    functionPermissions: [
      ({ user }) =>
        user.groupMemberships?.includes(ALLOCATION_COMMITTEE_GROUP) === true ||
        user.roles?.includes('GRANTEE')
    ],
    isTranslatable: true,
    label: <Trans>NAVIGATIONS_BECOME_EXTERNAL_REVIEWER</Trans>
  },
  {
    path: GRANTS_PATH.ASSIGNED_REVIEWS,
    component: AssignedOpportunities,
    auth: authRoles.reviewer,
    isTranslatable: true,
    label: <Trans>NAVIGATIONS_ASSIGNED_REVIEWS</Trans>,
    showCollapseBtn: true
  },
  {
    path: `${GRANTS_PATH.EXTERNAL_REVIEW_SURVEY}/:id/:mode`,
    component: ExternalReviewSurvey,
    auth: authRoles.reviewer,
    isTranslatable: true,
    isConditionable: true,
    id: 'ExternalReviewSurvey',
    label: <Trans>GRANTS_ROUTES_EXTERNAL_REVIEW_LABEL</Trans>,
    showCollapseBtn: true,
    collapseByDefault: true
  },
  {
    path: `${GRANTS_PATH.ACCEPT_REVIEW}/:id`,
    component: AcceptReviewRequest,
    auth: authRoles.reviewer,
    isTranslatable: true,
    label: <Trans>GRANTS_ROUTES_ACCEPT_EXTERNAL_REVIEW_LABEL</Trans>
  },
  {
    path: `${GRANTS_PATH.PRINT}/:id`,
    component: PrintView
  },
  {
    path: GRANTS_PATH.ORGANIZATIONS,
    component: Organizations,
    isTranslatable: true,
    label: <Trans>GRANTS_ROUTES_MANAGE_ORGANIZATIONS_LABEL</Trans>
  },
  {
    path: `${GRANTS_PATH.MANAGE_MEMBERS}/:id?`,
    component:
      sfOauthConfig.isInternal
        ? () => <Redirect to={NOT_FOUND_PATH} />
        : ManageOrganizationMembers,
    isTranslatable: true,
    label: <Trans>GRANTS_ROUTES_MANAGE_USERS_LABEL</Trans>,
    showCollapseBtn: true
  },
  {
    path: GRANTS_PATH.USER_PROFILE,
    component:
      sfOauthConfig.isInternal
        ? () => <Redirect to={NOT_FOUND_PATH} />
        : ApplicantInformation,
    auth: authRoles.user,
    showCollapseBtn: true,
    collapseByDefault: true
  },
  {
    path: GRANTS_PATH.JOIN_ORGANIZATION,
    component:
      sfOauthConfig.isInternal
        ? () => <Redirect to={NOT_FOUND_PATH} />
        : JoinOrganization,
    auth: authRoles.grantee,
    isTranslatable: true,
    label: <Trans>GRANTS_ROUTES_JOIN_ORGANIZATION_LABEL</Trans>
  },
  {
    path: '/grants/faq',
    component: FAQTab,
    showCollapseBtn: true,
    auth: authRoles.grantee,
    isTranslatable: true,
    label: <Trans>GRANTS_ROUTES_FAQ_LABEL</Trans>
  },
  {
    path: '/grants/faq-editor',
    component: FAQEditor,
    showCollapseBtn: true,
    auth: authRoles.grantee,
    isTranslatable: true,
    label: <Trans>GRANTS_ROUTES_FAQ_EDITOR_LABEL</Trans>
  }
]

export default grantsRoutes
